import { Alert, Button, Drawer, Form, Input, Modal, Popconfirm, Skeleton, Space, Switch, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  disableTeamMember,
  getTeamMember,
  patchTeamMember,
  resetTeamMemberPassword,
  restoreTeamMember,
  teamErrorMessage,
} from './teamApi'
import type { TeamMember } from './teamTypes'
import { TeamAvatar, useTeamFormat } from './teamUi'

const MIN_PASSWORD = 8

interface Props {
  workspaceId: string
  userId: string | null
  onClose: () => void
  /** Called after any successful write so the list + counts refresh. */
  onChanged: () => void
}

/**
 * VistoeraTASK-00525 — member detail / management drawer. Which buttons show is
 * driven by the `can_*` flags the SERVER computed for this exact (actor, target)
 * pair; the same rules are enforced again on every write endpoint.
 */
export default function MemberDrawer({ workspaceId, userId, onClose, onChanged }: Props) {
  const { t } = useTranslation('team')
  const { dateTime, num } = useTeamFormat()
  const [member, setMember] = useState<TeamMember | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [resetOpen, setResetOpen] = useState(false)
  const [resetForm] = Form.useForm<{ new_password: string; must_change_password: boolean }>()

  const load = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const res = await getTeamMember(workspaceId, userId)
      setMember(res.data)
      setName(res.data.display_name)
    } catch (e) {
      setError(teamErrorMessage(e, t('drawer.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, userId, t])

  useEffect(() => {
    setMember(null)
    if (userId) void load()
  }, [userId, load])

  const run = async (key: string, fn: () => Promise<{ data: TeamMember }>, okText: string) => {
    setBusy(key)
    try {
      const res = await fn()
      setMember(res.data)
      setName(res.data.display_name)
      void message.success(okText)
      onChanged()
      return true
    } catch (e) {
      void message.error(teamErrorMessage(e, t('drawer.actionFailed')))
      return false
    } finally {
      setBusy(null)
    }
  }

  const submitReset = async () => {
    if (!member) return
    const v = await resetForm.validateFields()
    const ok = await run(
      'reset',
      () => resetTeamMemberPassword(workspaceId, member.user_id, v),
      t('drawer.resetDone'),
    )
    if (ok) {
      resetForm.resetFields()
      setResetOpen(false)
    }
  }

  const m = member
  const isDisabled = m?.status === 'disabled'
  const legacy = !!m && !m.is_managed

  return (
    <Drawer
      open={!!userId}
      onClose={onClose}
      size={460}
      title={t('drawer.title')}
      destroyOnHidden
      data-testid="team-member-drawer"
    >
      {loading && !m && <Skeleton active avatar paragraph={{ rows: 8 }} />}
      {error && !m && <Alert type="error" showIcon title={error} />}
      {m && (
        <div className="tm-drawer">
          <div className="tm-drawer-head">
            <TeamAvatar name={m.display_name} size={56} round />
            <div>
              <div className="tm-drawer-name">{m.display_name}</div>
              <div className="tm-hint">{m.email}</div>
              <Space size={6} style={{ marginTop: 6 }}>
                <span className={`tm-tag tm-tag-${m.role_key}`}>{t(`role.${m.role_key}`)}</span>
                <span className={`tm-tag ${isDisabled ? 'tm-tag-off' : 'tm-tag-on'}`}>
                  {isDisabled ? t('status.disabled') : t('status.active')}
                </span>
              </Space>
            </div>
          </div>

          <dl className="tm-dl">
            <dt>{t('drawer.accountType')}</dt>
            <dd>{m.is_managed ? t('drawer.managed') : t('drawer.legacy')}</dd>
            <dt>{t('drawer.joinedAt')}</dt>
            <dd>{dateTime(m.joined_at)}</dd>
            <dt>{t('drawer.lastLogin')}</dt>
            <dd>{m.last_login_at ? dateTime(m.last_login_at) : t('drawer.neverLoggedIn')}</dd>
            {m.must_change_password && (
              <>
                <dt>{t('drawer.password')}</dt>
                <dd>{t('drawer.mustChangePending')}</dd>
              </>
            )}
          </dl>

          <div className="tm-section-title">{t('drawer.usageTitle')}</div>
          <div className="tm-usage-grid">
            <div>
              <strong>{num(m.month_usage?.images)}</strong>
              <span>{t('drawer.images')}</span>
            </div>
            <div>
              <strong>{num(m.month_usage?.videos)}</strong>
              <span>{t('drawer.videos')}</span>
            </div>
            <div>
              <strong>{num(m.month_usage?.ai_chats)}</strong>
              <span>{t('drawer.aiChats')}</span>
            </div>
            <div>
              <strong>{m.month_credits_used === null ? '—' : num(m.month_credits_used)}</strong>
              <span>{t('drawer.credits')}</span>
            </div>
          </div>
          {m.month_credits_used === null && <div className="tm-hint">{t('drawer.creditsNoAttribution')}</div>}

          <div className="tm-section-title">{t('drawer.manage')}</div>
          {!m.can_manage ? (
            <Alert type="info" showIcon title={m.is_self ? t('drawer.selfNote') : t('drawer.noPermission')} />
          ) : (
            <div className="tm-manage">
              {legacy && <Alert type="info" showIcon title={t('drawer.legacyNote')} style={{ marginBottom: 12 }} />}

              {m.is_managed && (
                <div className="tm-manage-row">
                  <Input
                    value={name}
                    maxLength={100}
                    onChange={(e) => setName(e.target.value)}
                    aria-label={t('drawer.name')}
                    data-testid="team-member-name-input"
                  />
                  <Button
                    loading={busy === 'name'}
                    disabled={!name.trim() || name.trim() === m.display_name}
                    onClick={() =>
                      void run('name', () => patchTeamMember(workspaceId, m.user_id, { display_name: name.trim() }), t('drawer.nameSaved'))
                    }
                  >
                    {t('drawer.saveName')}
                  </Button>
                </div>
              )}

              {m.can_set_role && !isDisabled && (
                <div className="tm-manage-row">
                  <span className="tm-manage-label">{t('drawer.roleLabel')}</span>
                  {m.role_key === 'admin' ? (
                    <Popconfirm
                      title={t('drawer.revokeConfirm')}
                      okText={t('common.confirm')}
                      cancelText={t('common.cancel')}
                      onConfirm={() =>
                        void run('role', () => patchTeamMember(workspaceId, m.user_id, { role: 'editor' }), t('drawer.revoked'))
                      }
                    >
                      <Button loading={busy === 'role'} data-testid="team-revoke-admin">
                        {t('drawer.revokeAdmin')}
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Popconfirm
                      title={t('drawer.appointConfirm')}
                      okText={t('common.confirm')}
                      cancelText={t('common.cancel')}
                      onConfirm={() =>
                        void run('role', () => patchTeamMember(workspaceId, m.user_id, { role: 'admin' }), t('drawer.appointed'))
                      }
                    >
                      <Button loading={busy === 'role'} data-testid="team-appoint-admin">
                        {t('drawer.appointAdmin')}
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              )}

              {m.can_reset_password && (
                <div className="tm-manage-row">
                  <Button onClick={() => setResetOpen(true)} data-testid="team-reset-password">
                    {t('drawer.resetPassword')}
                  </Button>
                </div>
              )}

              <div className="tm-manage-row">
                {isDisabled ? (
                  <Button
                    type="primary"
                    loading={busy === 'restore'}
                    onClick={() => void run('restore', () => restoreTeamMember(workspaceId, m.user_id), t('drawer.restored'))}
                    data-testid="team-restore-member"
                  >
                    {t('drawer.restore')}
                  </Button>
                ) : (
                  <Popconfirm
                    title={t('drawer.disableConfirm')}
                    description={t('drawer.disableDesc')}
                    okText={t('common.confirm')}
                    okButtonProps={{ danger: true }}
                    cancelText={t('common.cancel')}
                    onConfirm={() => void run('disable', () => disableTeamMember(workspaceId, m.user_id), t('drawer.disabled'))}
                  >
                    <Button danger loading={busy === 'disable'} data-testid="team-disable-member">
                      {t('drawer.disable')}
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        open={resetOpen}
        title={t('drawer.resetTitle')}
        okText={t('drawer.resetSubmit')}
        cancelText={t('common.cancel')}
        confirmLoading={busy === 'reset'}
        onOk={() => void submitReset()}
        onCancel={() => {
          resetForm.resetFields()
          setResetOpen(false)
        }}
        destroyOnHidden
      >
        <p className="tm-hint" style={{ marginTop: 0 }}>
          {t('drawer.resetIntro')}
        </p>
        <Form form={resetForm} layout="vertical" initialValues={{ must_change_password: true }} autoComplete="off">
          <Form.Item
            name="new_password"
            label={t('drawer.newPassword')}
            rules={[
              { required: true, message: t('create.passwordRequired') },
              { min: MIN_PASSWORD, message: t('create.passwordHint', { min: MIN_PASSWORD }) },
            ]}
          >
            <Input.Password autoComplete="new-password" data-testid="team-reset-new-password" />
          </Form.Item>
          <Form.Item name="must_change_password" label={t('create.mustChange')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  )
}
